export function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function computeSoumErrors(s) {
  const isValidEmail = e =>
    typeof e === "string" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim());

  if (!s) return {};
  return {
    numeroBCE: !s.numeroBCE || s.numeroBCE.trim() === "",
    nom: !s.nom || s.nom.trim() === "",
    personneContact: !s.personneContact || s.personneContact.trim() === "",
    civility: !s.civility || s.civility.trim() === "",
    job: !s.job || s.job.trim() === "",
    emails:
      !Array.isArray(s.emails) || s.emails.length === 0
        ? [true]
        : s.emails.map(e => !isValidEmail(e)),
  };
}

export function isSoumissionnaireComplet(s) {
  const err = computeSoumErrors(s);
  return (
    !err.numeroBCE
    && !err.nom
    && !err.personneContact
    && !err.civility
    && !err.job
    && Array.isArray(err.emails)
    && err.emails.length > 0
    && err.emails.every(v => v === false)
  );
}

export function isDescriptionEmpty(html) {
  return html.replace(/<(.|\n)*?>/g, "").trim().length === 0;
}

export function isCriterionComplete(c) {
  if (!c.label.trim()) return false;
  const pondVal = parseFloat(c.pond.toString().replace(",", "."));
  if (isNaN(pondVal) || pondVal < 1 || pondVal > 100) return false;
  if (isDescriptionEmpty(c.description)) return false;
  return true;
}

export function formatEuro(value) {
  if (!value) return "";
  const numeric = value.replace(/\D/g, "");
  const number = parseInt(numeric, 10);
  if (isNaN(number)) return "";
  return new Intl.NumberFormat("fr-BE").format(number);
}

export function sanitizePond(raw) {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const num = Math.min(100, Math.max(1, parseInt(digits, 10)));
  return String(num);
}

export function getOrdinal(index) {
  const ordinals = ["premier", "deuxième", "troisième", "quatrième", "cinquième"];
  return ordinals[index] || `${index + 1}e`;
}

export function replaceSmartQuotes(text) {
  return text.replace(/[“”«»]/g, '"');
}

export function removeAllConsecutiveEmptyParagraphs(html) {
  return html.replace(/(<p[^>]*>(?:\s|&nbsp;|<o:p>(&nbsp;)?<\/o:p>|<br\s*\/?>)*<\/p>\s*){2,}/gi, '<p>&nbsp;</p>');
}

export function removeExtraBlankLines(html) {
  let cleaned = html.replace(/(\s*<br\s*\/?>\s*){2,}/gi, '<br />');
  cleaned = cleaned.replace(/(<p[^>]*>(?:\s|&nbsp;|<o:p>(&nbsp;)?<\/o:p>|<br\s*\/?>)*<\/p>\s*){2,}/gi, '<p>&nbsp;</p>');
  cleaned = cleaned.replace(/(<br\s*\/?>\s*)+(<p[^>]*>(?:\s|&nbsp;|<o:p>(&nbsp;)?<\/o:p>|<br\s*\/?>)*<\/p>\s*){1,}/gi, '<br />\n<p>&nbsp;</p>');
  cleaned = cleaned.replace(/^((<br\s*\/?>|\s|&nbsp;|<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>|<o:p>(&nbsp;)?<\/o:p>)*<\/p>|<o:p>(&nbsp;)?<\/o:p>|\r?\n)+)/gi, '');
  cleaned = cleaned.replace(/((<br\s*\/?>|\s|&nbsp;|<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>|<o:p>(&nbsp;)?<\/o:p>)*<\/p>|<o:p>(&nbsp;)?<\/o:p>|\r?\n)+)$/gi, '');
  cleaned = cleaned.replace(/(<p[^>]*>(?:\s|&nbsp;|<o:p>(&nbsp;)?<\/o:p>|<br\s*\/?>)*<\/p>\s*)+$/gi, '');
  cleaned = cleaned.replace(/(<br\s*\/?>\s*)+$/gi, '');
  cleaned = cleaned.replace(/(<o:p>(&nbsp;)?<\/o:p>\s*)+$/gi, '');
  return cleaned;
}

function decodeHTMLEntities(str) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

export function cleanHtml(html) {
  html = html.replace(/<span[^>]*>(.*?)<\/span>/gi, '$1');
  html = html.replace(/<br\s*[^>]*>/gi, '<br />');
  html = html.replace(/<strike>/gi, '<s>').replace(/<\/strike>/gi, '</s>');
  html = html.replace(/<!--[\s\S]*?-->/g, '');
  html = html.replace(/<(?!(a|img|u|s)\b)([a-z0-9]+)\b([^>]*)>/gi, '<$2>');
  html = html.replace(/<a\s+([^>]*)>/gi, (match, attrs) => {
    const hrefMatch = attrs.match(/href\s*=\s*(['"][^'"]*['"]|[^\s>]+)/i);
    return hrefMatch ? `<a ${hrefMatch[0]}>` : '<a>';
  });
  html = html.replace(/<img\s+([^>]*)>/gi, (match, attrs) => {
    let srcMatch = attrs.match(/src\s*=\s*(['"][^'"]*['"]|[^\s>]+)/i);
    let altMatch = attrs.match(/alt\s*=\s*(['"][^'"]*['"]|[^\s>]+)/i);
    let out = '<img';
    if (srcMatch) out += ` ${srcMatch[0]}`;
    if (altMatch) out += ` ${altMatch[0]}`;
    out += '>';
    return out;
  });
  html = html.replace(/([“”«»"]|&ldquo;|&rdquo;|&quot;|&laquo;|&raquo;)+/g, '"');
  html = html.replace(/<code>(.*?)<\/code>/gis, '$1');
  html = html.replace(/<pre>(.*?)<\/pre>/gis, '$1');
  html = html.replace(/<h[1-6][^>]*>/gi, '<p>');
  html = html.replace(/<\/h[1-6]>/gi, '</p>');
  html = html.replace(/ style="[^"]*"/gi, '');
  html = html.replace(/ class="[^"]*"/gi, '');
  html = html.replace(/<button[^>]*>[\s\S]*?<\/button>/gi, '');
  html = html.replace(/<div[^>]*>/gi, '');
  html = html.replace(/<\/div>/gi, '');
  for (let i = 0; i < 15; i++) {
    html = html.replace(/<\/ol>\s*<ol>/gi, '');
    html = html.replace(/<\/ul>\s*<ul>/gi, '');
  }
  html = html.replace(/(\r?\n){2,}/g, '\n');
  html = removeExtraBlankLines(html);
  html = removeAllConsecutiveEmptyParagraphs(html);
  html = html.replace(/([.!?…;:])\s+((?:<\/[a-z0-9]+>)+)/gi, '$1$2');
  html = html.replace(/<span>(.*?)<\/span>/gi, '$1');
  html = html.replace(
    /^<p>([A-ZÉÈÊÀÂÙÛÎÔÇÄËÏÖÜŸ0-9\s\-!?,:;.'"]{10,})<\/p>/m,
    '<h1>$1</h1>',
  );
  html = html.replace(/((?:<p>\s*[•*-]\s*.*?<\/p>\s*){2,})/g, (m) => {
    let items = m.match(/<p>\s*[•*-]\s*(.*?)<\/p>/g);
    if (!items) return m;
    return `<ul>\n${items.map(x => '<li>' + x.replace(/<p>\s*[•*-]\s*(.*?)<\/p>/, '$1') + '</li>').join('\n')}\n</ul>`;
  });
  html = html.replace(/((?:<p>\s*\d+[.)]\s*.*?<\/p>\s*){2,})/g, (m) => {
    let items = m.match(/<p>\s*\d+[.)]\s*(.*?)<\/p>/g);
    if (!items) return m;
    return `<ol>\n${items.map(x => '<li>' + x.replace(/<p>\s*\d+[.)]\s*(.*?)<\/p>/, '$1') + '</li>').join('\n')}\n</ol>`;
  });
  html = html.replace(/<p>\s*["«“](.+?)["»”]\s*<\/p>/g, '<blockquote><p>$1</p></blockquote>');
  html = html.replace(/((?:<p>.*\|.*<\/p>\s*){2,})/g, (m) => {
    let rows = m.trim().split(/<\/p>\s*<p>/g).map(r =>
      '<tr>' + r.replace(/<.?p>/g, '').split('|').map(cell => `<td>${cell.trim()}</td>`).join('') + '</tr>',
    );
    return `<table><tbody>${rows.join('')}</tbody></table>`;
  });
  html = html.replace(/<p>([^<]*?)((https?:\/\/|www\.)[^\s<]+)([^<]*?)<\/p>/g, (m, p1, url, p3, p4) => {
    let link = url.startsWith('www.') ? 'http://' + url : url;
    return `<p>${p1}<a href="${link}">${url}</a>${p4}</p>`;
  });
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/&nbsp;/gi, ' ');
  html = html.replace(/\u202F/g, ' ');
  html = decodeHTMLEntities(html);
  html = html.replace(/([‘’']|&lsquo;|&rsquo;)+/g, "'");
  html = html.replace(/<p[^>]*>(?:\s|&nbsp;)*<\/p>/gi, '<br />');
  html = html.replace(/[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]+(<\/[a-z0-9]+>)/gi, '$1');
  html = html.trim();
  return html;
}

export function todayISO() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toISODate(dateObj) {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatEuroGroup(valueNumber) {
  if (!Number.isFinite(valueNumber)) return "";
  try {
    return new Intl.NumberFormat("fr-BE", {
      useGrouping: true,
      maximumFractionDigits: 0,
    }).format(valueNumber);
  } catch {
    return String(valueNumber).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }
}

export function formatCurrencyEUR(value) {
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

export function normalizeName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr-BE")
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeEnterpriseNumber(n) {
  return String(n || "").replace(/\D/g, "");
}

export function findOfferForBidder(bidder, procurementOffers) {
  if (!bidder || !Array.isArray(procurementOffers)) return null;

  const bidderUuid = bidder.uuid || bidder?.enterprise?.uuid || null;
  const bidderNumber = normalizeEnterpriseNumber(
    bidder.enterpriseNumber || bidder.bce || bidder?.enterprise?.enterpriseNumber,
  );
  const bidderName = normalizeName(bidder.name || bidder?.enterprise?.name);

  if (bidderUuid) {
    const byUuid = procurementOffers.find(o => o?.uuidEnterprise === bidderUuid);
    if (byUuid) return byUuid;
  }
  if (bidderNumber) {
    const byNumber = procurementOffers.find(
      o => normalizeEnterpriseNumber(o?.enterprise?.enterpriseNumber) === bidderNumber,
    );
    if (byNumber) return byNumber;
  }
  if (bidderName) {
    const byName = procurementOffers.find(o => normalizeName(o?.enterprise?.name) === bidderName);
    if (byName) return byName;
  }
  return null;
}

export const getDetailValue = v => (typeof v === "string" ? v : "");

export function buildScoresTemplate(criteria = []) {
  const obj = {};
  criteria.forEach((c) => {
    obj[c.id] = { score: "", detail: "" };
  });
  return obj;
}
